import {
  type RefObject,
  createContext,
  use,
  useContext,
  useMemo,
  useRef,
} from 'react'
import type { RootStore } from '@/RootStore'
import { ValueRetrievablePromise } from '@/utils/value-retrievable-promise'
import { isPromiseLike } from '@/utils/promise-util'

export const RootStoreContext = createContext<RootStore | null>(null)

/**
 * Provides access to the root store.
 * Only access internals from the local feature object, and public members
 * from other features.
 */
export function useRootStore(): RootStore {
  const rootStore = useContext(RootStoreContext)
  return rootStore! // SAFETY: will always be provided by the app
}

/**
 * A function that accepts a root store and selects the needed features.
 */
export type FeaturesSelector<T extends Record<string, unknown>> = (
  rootStore: RootStore,
) => T

/**
 * The selected features with promises resolved.
 */
export type SelectedFeatures<T> =
  T extends Record<string, unknown>
    ? {
        [K in keyof T]: T[K] extends PromiseLike<infer R> ? R : T[K]
      }
    : T extends Promise<infer R>
      ? R
      : never

/**
 * Calls {@link selector} with the root store as the only argument.
 * If any of the top-level properties on the returned object
 * from {@link selector} are promises, the component will be suspended
 * until they resolve.
 *
 * @param selector
 */
export function useFeatures<T extends Record<string, unknown>>(
  selector: FeaturesSelector<T>,
): SelectedFeatures<T> {
  const rootStore = useRootStore()
  const selected = useMemo(
    () => selector(rootStore),
    // We only want the selector to run once, since all it's doing is picking out features
    // from the root store.
    // eslint-disable-next-line react-hooks/exhaustive-deps
    [],
  )

  // We'll need to set up a promise that loads all the promise-based properties
  // of the selection.
  const promiseRef = useRef<PromiseLike<SelectedFeatures<T>> | null>(null)

  // Triggers a suspense boundary.
  // eslint-disable-next-line react-hooks/refs -- The ref holds the Suspense promise across renders; `needsImpl` reads it on purpose.
  return needsImpl(selected, promiseRef)
}

/**
 * Uses Reacts `use` to wait for promises on the specified `props`
 * to resolve.
 *
 * @param props
 * @param promiseRef
 */
export function needsImpl<T extends Record<string, unknown>>(
  props: T,
  promiseRef: RefObject<PromiseLike<unknown> | null>,
): SelectedFeatures<T> {
  // Note regarding React Suspense: to avoid the suspense fallback from showing
  // every time we navigate between views even when the chunk has already
  // been loaded, we need to make sure that the promise passed to `use` resolves
  // immediately and synchronously. To do that, the feature loader returns
  // promise-like objects from which we can retrieve the resolved
  // value synchronously if the promise has already resolved.
  // That way, if all the values of `props` have already been resolved,
  // then we won't need to suspend.

  /* eslint-disable @typescript-eslint/no-explicit-any,@typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment */
  if (!promiseRef.current) {
    // This is the final object we'll be returning.
    const result: any = {}
    const promises = []
    for (const key in props) {
      const capturedKey = key
      const value = props[key]

      if (isPromiseLike<any>(value)) {
        // If the promise's value can be retrieved, we can skip the
        // suspense fallback.
        if (ValueRetrievablePromise.isRetrievable(value)) {
          result[key] = ValueRetrievablePromise.getResolved(value)
        } else {
          promises.push(
            value.then((r: any) => {
              // Populate the result object.
              result[capturedKey] = r
            }),
          )
        }
      } else {
        result[key] = value
      }
    }

    // Wait for all the promise-based props to resolve
    // if we have any.
    if (promises.length > 0) {
      promiseRef.current = ValueRetrievablePromise.from(
        Promise.all(promises).then(() => result as SelectedFeatures<T>),
      )
      // eslint-disable-next-line react-hooks/rules-of-hooks
      return use(promiseRef.current) as SelectedFeatures<T>
    }

    // In this case, there were no promises to resolve, but we need a promise
    // to `use`, so create one that resolves immediately and synchronously to
    // avoid the suspense fallback.
    promiseRef.current = ValueRetrievablePromise.fromValue(result)
  }

  // eslint-disable-next-line react-hooks/rules-of-hooks
  return use(promiseRef.current) as SelectedFeatures<T>

  /* eslint-enable @typescript-eslint/no-explicit-any,@typescript-eslint/no-unsafe-member-access,@typescript-eslint/no-unsafe-assignment */
}
